import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isToday, addMonths, subMonths, startOfMonth, endOfMonth, parse, isValid } from 'date-fns';

const DatePicker = ({ value, onChange, minDate, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value ? new Date(value) : new Date());
  const [inputValue, setInputValue] = useState(value || '');
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get today's date in local timezone (YYYY-MM-DD format)
  const getTodayLocal = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Parse minDate or use today
  const todayLocal = getTodayLocal();
  const minDateLocal = minDate || todayLocal;

  // Update inputValue when value prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Position dropdown and handle click outside
  useEffect(() => {
    if (!isOpen || !inputRef.current || !dropdownRef.current) return;

    const updatePosition = () => {
      if (!inputRef.current || !dropdownRef.current) return;
      
      const inputRect = inputRef.current.getBoundingClientRect();
      const dropdown = dropdownRef.current;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownHeight = 350; // Approximate height of calendar
      const dropdownWidth = 280; // Approximate width of calendar
      
      // Calculate position below input (in viewport coordinates)
      let topViewport = inputRect.bottom + 4;
      let leftViewport = inputRect.left;
      
      // Check if dropdown would go below viewport
      if (topViewport + dropdownHeight > viewportHeight) {
        // Position above input instead
        topViewport = inputRect.top - dropdownHeight - 4;
        // If still doesn't fit above, position at bottom of viewport
        if (topViewport < 0) {
          topViewport = viewportHeight - dropdownHeight - 4;
          // Ensure it doesn't go above viewport
          if (topViewport < 4) {
            topViewport = 4;
          }
        }
      }
      
      // Check if dropdown would go off right edge
      if (leftViewport + dropdownWidth > viewportWidth) {
        leftViewport = viewportWidth - dropdownWidth - 4;
      }
      
      // Check if dropdown would go off left edge
      if (leftViewport < 4) {
        leftViewport = 4;
      }
      
      // Convert to fixed position (relative to viewport, not document)
      dropdown.style.position = 'fixed';
      dropdown.style.top = `${topViewport}px`;
      dropdown.style.left = `${leftViewport}px`;
      dropdown.style.zIndex = '9999';
      dropdown.style.maxHeight = `${Math.min(dropdownHeight, viewportHeight - topViewport - 8)}px`;
      dropdown.style.overflowY = 'auto';
      dropdown.style.overflowX = 'hidden';
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Update currentMonth when value changes
  useEffect(() => {
    if (value) {
      setCurrentMonth(new Date(value));
    }
  }, [value]);

  const handleDateSelect = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setInputValue(dateStr);
    onChange(dateStr);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Try to parse the date
    if (newValue.length === 10) { // YYYY-MM-DD format
      const parsedDate = parse(newValue, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        const dateStr = format(parsedDate, 'yyyy-MM-dd');
        // Validate against minDate
        if (dateStr >= minDateLocal) {
          onChange(dateStr);
          setCurrentMonth(parsedDate);
        }
      }
    }
  };

  const handleInputBlur = () => {
    // Validate and format on blur
    if (inputValue) {
      const parsedDate = parse(inputValue, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        const dateStr = format(parsedDate, 'yyyy-MM-dd');
        if (dateStr >= minDateLocal) {
          setInputValue(dateStr);
          onChange(dateStr);
          setCurrentMonth(parsedDate);
        } else {
          // Reset to current value if invalid
          setInputValue(value || '');
        }
      } else {
        // Reset to current value if invalid format
        setInputValue(value || '');
      }
    } else {
      setInputValue(value || '');
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleTodayClick = () => {
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    // Only select today if it's not disabled
    if (todayStr >= minDateLocal) {
      setCurrentMonth(today);
      handleDateSelect(today);
    }
  };

  // Get calendar days for the current month
  const getCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    
    // Get the Monday that starts the week containing monthStart
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    
    // Get the Sunday that ends the week containing monthEnd
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  };

  const calendarDays = getCalendarDays();
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const isDateDisabled = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Disable dates before minDate (allow minDate itself)
    return dateStr < minDateLocal;
  };

  const isDateSelected = (date) => {
    if (!value) return false;
    return isSameDay(date, new Date(value));
  };

  const calendarDropdown = isOpen ? (
    <div 
      ref={dropdownRef} 
      className="bg-gray-800 border border-gray-600 rounded-lg shadow-xl p-4 min-w-[280px] max-w-[calc(100vw-8px)]"
      style={{ touchAction: 'manipulation' }}
    >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePrevMonth}
              className="p-1 rounded hover:bg-gray-700 text-gray-300 hover:text-white"
              aria-label="Previous month"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="text-white font-semibold">
              {format(currentMonth, 'MMMM yyyy')}
            </div>
            
            <button
              onClick={handleNextMonth}
              className="p-1 rounded hover:bg-gray-700 text-gray-300 hover:text-white"
              aria-label="Next month"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Week day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-semibold text-gray-400 py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, idx) => {
              const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
              const isDisabled = isDateDisabled(day);
              const isSelected = isDateSelected(day);
              const isTodayDate = isToday(day);

              return (
                <button
                  key={idx}
                  onClick={() => !isDisabled && handleDateSelect(day)}
                  disabled={isDisabled}
                  className={`
                    p-2 text-sm rounded transition-colors
                    ${isCurrentMonth ? 'text-white' : 'text-gray-600'}
                    ${isDisabled 
                      ? 'cursor-not-allowed opacity-30' 
                      : 'cursor-pointer hover:bg-gray-700'
                    }
                    ${isSelected 
                      ? 'bg-blue-600 text-white font-semibold' 
                      : ''
                    }
                    ${isTodayDate && !isSelected 
                      ? 'ring-2 ring-blue-400' 
                      : ''
                    }
                  `}
                >
                  {format(day, 'd')}
                </button>
              );
            })}
          </div>

          {/* Today button */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <button
              onClick={handleTodayClick}
              className="w-full px-3 py-2 text-sm text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded transition-colors"
            >
              Today
            </button>
          </div>
        </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onClick={() => setIsOpen(!isOpen)}
          placeholder="Select date"
          className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 cursor-text"
        />
        {/* Calendar icon indicator */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-300 focus:outline-none"
          aria-label="Open calendar"
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
        </button>
      </div>
      
      {isOpen && typeof document !== 'undefined' && createPortal(calendarDropdown, document.body)}
    </div>
  );
};

export default DatePicker;

